/**
 * @fileoverview API client for room management operations
 * @author Hospital Room Scheduler Team
 * @created 2025-01-08
 */

import { TokenManager } from '../interceptors';

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:7991/api/v1';

// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Create headers for API requests
 * @returns Headers object
 */
function createHeaders(): HeadersInit {
  const token = TokenManager.getAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

/**
 * Interface for Room entity with basic information
 */
export interface Room {
  id: string;
  number: string;
  name: string;
  floorId: string;
  capacity: number;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Interface for Room with floor and building details
 */
export interface RoomWithDetails extends Room {
  floor: {
    id: string;
    number: number;
    name: string;
    buildingId: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    building: {
      id: string;
      name: string;
      description?: string;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    };
  };
}

/**
 * Interface for Room with statistics
 */
export interface RoomWithStats extends RoomWithDetails {
  _count: {
    assignments: number;
  };
  assignmentsCount?: number;
}

/**
 * Interface for creating a new room
 */
export interface CreateRoomData {
  number: string;
  name: string;
  floorId: string;
  capacity: number;
  description?: string;
  allowedSpecialties?: string[]; // Gap G3-3: Multi-select specialties
  defaultBuffer?: number; // Gap G3-4: Buffer minutes between sessions
}

/**
 * Interface for updating a room
 */
export interface UpdateRoomData {
  number?: string;
  name?: string;
  floorId?: string;
  capacity?: number;
  description?: string;
  isActive?: boolean;
  allowedSpecialties?: string[]; // Gap G3-3: Multi-select specialties
  defaultBuffer?: number; // Gap G3-4: Buffer minutes between sessions
}

/**
 * Interface for room query parameters
 */
export interface GetRoomsParams {
  page?: number;
  limit?: number;
  search?: string;
  floorId?: string;
  buildingId?: string;
  isActive?: boolean;
  sortBy?: 'name' | 'number' | 'capacity' | 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Interface for room statistics query parameters
 */
export interface GetRoomStatsParams {
  buildingId?: string;
  floorId?: string;
}

/**
 * Interface for pagination metadata
 */
export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

/**
 * Interface for paginated rooms response
 */
export interface PaginatedRoomsResponse {
  data: RoomWithDetails[];
  pagination: PaginationMeta;
}

/**
 * Interface for room statistics
 */
export interface RoomStats {
  totalRooms: number;
  activeRooms: number;
  inactiveRooms: number;
  totalCapacity: number;
  averageCapacity: number;
  roomsByFloor: Array<{
    floorId: string;
    floorName: string;
    buildingName: string;
    roomCount: number;
    totalCapacity: number;
  }>;
  capacityDistribution: Array<{
    capacityRange: string;
    count: number;
    percentage: number;
  }>;
}

/**
 * Interface for API response wrapper
 */
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  meta?: {
    message?: string;
    timestamp?: string;
  };
}

/**
 * Get all rooms with filtering, sorting, and pagination
 * @param params - Query parameters for filtering and pagination
 * @returns Promise resolving to paginated rooms response
 */
export const getRooms = async (params: GetRoomsParams = {}): Promise<PaginatedRoomsResponse> => {
  const queryParams = new URLSearchParams({
    page: (params.page || 1).toString(),
    limit: (params.limit || 20).toString(),
    sortBy: params.sortBy || 'name',
    sortOrder: params.sortOrder || 'asc',
    ...(params.search && { search: params.search }),
    ...(params.floorId && { floorId: params.floorId }),
    ...(params.buildingId && { buildingId: params.buildingId }),
    ...(params.isActive !== undefined && { isActive: params.isActive.toString() })
  });

  const response = await fetch(`${API_BASE_URL}/rooms?${queryParams}`, {
    method: 'GET',
    headers: createHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  const data: ApiResponse<PaginatedRoomsResponse> = await response.json();

  if (!data.success || !data.data) {
    throw new Error(data.error?.message || 'Failed to fetch rooms');
  }

  return data.data;
};

/**
 * Get a single room by ID
 * @param id - Room ID
 * @returns Promise resolving to room details
 */
export const getRoomById = async (id: string): Promise<RoomWithDetails> => {
  const response = await fetch(`${API_BASE_URL}/rooms/${id}`, {
    method: 'GET',
    headers: createHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  if (!data.success || !data.data) {
    throw new Error(data.error?.message || 'Failed to fetch room');
  }

  return data.data;
};

/**
 * Create a new room
 * @param data - Room creation data
 * @returns Promise resolving to created room
 */
export const createRoom = async (data: CreateRoomData): Promise<RoomWithDetails> => {
  const response = await fetch(`${API_BASE_URL}/rooms`, {
    method: 'POST',
    headers: createHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  const responseData = await response.json();
  if (!responseData.success || !responseData.data) {
    throw new Error(responseData.error?.message || 'Failed to create room');
  }

  return responseData.data;
};

/**
 * Update an existing room
 * @param id - Room ID
 * @param data - Room update data
 * @returns Promise resolving to updated room
 */
export const updateRoom = async (id: string, data: UpdateRoomData): Promise<RoomWithDetails> => {
  const response = await fetch(`${API_BASE_URL}/rooms/${id}`, {
    method: 'PUT',
    headers: createHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  const responseData = await response.json();
  if (!responseData.success || !responseData.data) {
    throw new Error(responseData.error?.message || 'Failed to update room');
  }

  return responseData.data;
};

/**
 * Delete a room (soft delete)
 * @param id - Room ID
 * @returns Promise resolving to deletion result
 */
export const deleteRoom = async (id: string): Promise<{ message: string }> => {
  const response = await fetch(`${API_BASE_URL}/rooms/${id}`, {
    method: 'DELETE',
    headers: createHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  const responseData = await response.json();
  if (!responseData.success || !responseData.data) {
    throw new Error(responseData.error?.message || 'Failed to delete room');
  }

  return responseData.data;
};

/**
 * Get rooms by floor ID
 * @param floorId - Floor ID
 * @returns Promise resolving to list of rooms in the floor
 */
export const getRoomsByFloor = async (floorId: string): Promise<RoomWithDetails[]> => {
  const response = await fetch(`${API_BASE_URL}/rooms/floor/${floorId}`, {
    method: 'GET',
    headers: createHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  const responseData = await response.json();
  if (!responseData.success || !responseData.data) {
    throw new Error(responseData.error?.message || 'Failed to fetch rooms by floor');
  }

  return responseData.data;
};

/**
 * Get floors for a specific building
 * @param buildingId - Building ID
 * @returns Promise resolving to list of floors
 */
export const getFloorsForBuilding = async (buildingId: string): Promise<Array<{
  id: string;
  number: number;
  name: string;
  isActive: boolean;
}>> => {
  const response = await fetch(`${API_BASE_URL}/floors/building/${buildingId}`, {
    method: 'GET',
    headers: createHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  const responseData = await response.json();
  if (!responseData.success || !responseData.data) {
    throw new Error(responseData.error?.message || 'Failed to fetch floors for building');
  }

  // Handle both array and object response formats
  const floorsData = Array.isArray(responseData.data) ? responseData.data : responseData.data.floors || [];
  return floorsData;
};

/**
 * Get rooms for selection (dropdowns, etc.)
 * Fetches all rooms with pagination to handle cases where there are more than 100 rooms
 * @param token - Authentication token
 * @returns Promise resolving to list of rooms with details
 */
export const getRoomsForSelection = async (): Promise<RoomWithDetails[]> => {
  const allRooms: RoomWithDetails[] = [];
  let page = 1;
  let hasMore = true;

  while (hasMore) {
    const response = await fetch(`${API_BASE_URL}/rooms?page=${page}&limit=100&sortBy=name&sortOrder=asc`, {
      method: 'GET',
      headers: createHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data: ApiResponse<PaginatedRoomsResponse> = await response.json();

    if (!data.success || !data.data) {
      throw new Error(data.error?.message || 'Failed to fetch rooms for selection');
    }

    allRooms.push(...data.data.data);
    
    // Check if there are more pages
    hasMore = data.data.pagination.hasNext;
    page++;
    
    // Safety break to prevent infinite loops
    if (page > 10) {
      console.warn('getRoomsForSelection: Stopped pagination after 10 pages to prevent infinite loop');
      break;
    }
  }

  return allRooms;
};

/**
 * Get room statistics
 * @param params - Query parameters for filtering
 * @returns Promise resolving to room statistics
 */
export const getRoomStats = async (params: GetRoomStatsParams = {}): Promise<RoomStats> => {
  const queryParams = new URLSearchParams();
  if (params.buildingId) queryParams.append('buildingId', params.buildingId);
  if (params.floorId) queryParams.append('floorId', params.floorId);

  const url = `${API_BASE_URL}/rooms/stats?${queryParams.toString()}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: createHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  const responseData = await response.json();
  if (!responseData.success || !responseData.data) {
    throw new Error(responseData.error?.message || 'Failed to fetch room statistics');
  }

  return responseData.data;
};

/**
 * Format room for display in UI components
 * @param room - Room data
 * @returns Formatted room display string
 */
export const formatRoomForDisplay = (room: RoomWithDetails): string => {
  return `${room.number} - ${room.name} (${room.floor.building.name} - ${room.floor.name})`;
};

/**
 * Get room status color for UI components
 * @param room - Room data
 * @returns Color string for status indicator
 */
export const getRoomStatusColor = (room: Room): string => {
  if (!room.isActive) return 'red';
  return 'green';
};

/**
 * Get room capacity status color
 * @param room - Room data
 * @param currentOccupancy - Current number of people in room
 * @returns Color string for capacity indicator
 */
export const getRoomCapacityColor = (room: Room, currentOccupancy: number = 0): string => {
  const utilizationPercentage = (currentOccupancy / room.capacity) * 100;
  
  if (utilizationPercentage >= 100) return 'red';
  if (utilizationPercentage >= 80) return 'orange';
  if (utilizationPercentage >= 60) return 'yellow';
  return 'green';
};

/**
 * Get room capacity status text
 * @param room - Room data
 * @param currentOccupancy - Current number of people in room
 * @returns Status text for capacity indicator
 */
export const getRoomCapacityStatus = (room: Room, currentOccupancy: number = 0): string => {
  const utilizationPercentage = (currentOccupancy / room.capacity) * 100;
  
  if (utilizationPercentage >= 100) return 'At Capacity';
  if (utilizationPercentage >= 80) return 'Near Capacity';
  if (utilizationPercentage >= 60) return 'Moderate';
  return 'Available';
};

/**
 * Validate room data before submission
 * @param data - Room data to validate
 * @returns Validation result with errors if any
 */
export const validateRoomData = (data: CreateRoomData | UpdateRoomData): {
  isValid: boolean;
  errors: Record<string, string>;
} => {
  const errors: Record<string, string> = {};

  // Validate room number
  if (data['number'] !== undefined) {
    if (!data['number'].trim()) {
      errors['number'] = 'Room number is required';
    } else if (data['number'].length > 20) {
      errors['number'] = 'Room number cannot exceed 20 characters';
    }
  }

  // Validate room name
  if (data['name'] !== undefined) {
    if (!data['name'].trim()) {
      errors['name'] = 'Room name is required';
    } else if (data['name'].length > 100) {
      errors['name'] = 'Room name cannot exceed 100 characters';
    }
  }

  // Validate capacity
  if (data['capacity'] !== undefined) {
    if (data['capacity'] < 1) {
      errors['capacity'] = 'Capacity must be at least 1';
    } else if (data['capacity'] > 100) {
      errors['capacity'] = 'Capacity cannot exceed 100';
    }
  }

  // Validate description
  if (data['description'] !== undefined && data['description'].length > 500) {
    errors['description'] = 'Description cannot exceed 500 characters';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// =============================================================================
// ROOM DETAIL ENDPOINTS (Added: 07-10-2025 14:01:32)
// =============================================================================

/**
 * Get comprehensive room detail data
 * 
 * @param roomId - Room ID (UUID)
 * @param params - Optional query parameters
 * @param params.startDate - Start date for filtering (ISO format)
 * @param params.endDate - End date for filtering (ISO format)
 * @returns Promise with room detail data (room, schedule, blocks, conflicts, utilization)
 * 
 * Created: 07-10-2025 14:01:32
 */
export const getRoomDetail = async (
  roomId: string,
  params?: {
    startDate?: string;
    endDate?: string;
  }
): Promise<any> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const queryString = queryParams.toString();
    const url = `${API_BASE_URL}/rooms/${roomId}/detail${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: createHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to fetch room detail');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[Room API] Error fetching room detail:', error);
    throw error;
  }
};

/**
 * Get room schedule data
 * Fetches assignments and session times for a specific room and date range
 * 
 * @param roomId - Room ID (UUID)
 * @param startDate - Start date for filtering (ISO format)
 * @param endDate - End date for filtering (ISO format)
 * @returns Promise with room schedule data
 * 
 * Created: 08-10-2025 01:56:24
 */
export const getRoomSchedule = async (
  roomId: string,
  startDate: string,
  endDate: string
): Promise<any> => {
  try {
    const queryParams = new URLSearchParams({
      startDate,
      endDate,
    });

    const url = `${API_BASE_URL}/rooms/${roomId}/schedule?${queryParams.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: createHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to fetch room schedule');
    }

    return data.data;
  } catch (error) {
    console.error('[Room API] Error fetching room schedule:', error);
    throw error;
  }
};

/**
 * Get room session times
 * Fetches all configured session times for a specific room
 * 
 * @param roomId - Room ID (UUID)
 * @returns Promise with room session times data
 * 
 * Created: 08-10-2025 01:56:24
 */
export const getRoomSessionTimes = async (roomId: string): Promise<any> => {
  try {
    const url = `${API_BASE_URL}/rooms/${roomId}/session-times`;

    const response = await fetch(url, {
      method: 'GET',
      headers: createHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to fetch room session times');
    }

    return data.data;
  } catch (error) {
    console.error('[Room API] Error fetching room session times:', error);
    throw error;
  }
};

/**
 * Get room blocks
 * Fetches all maintenance/cleaning blocks for a specific room and date range
 * 
 * @param roomId - Room ID (UUID)
 * @param startDate - Start date for filtering (ISO format)
 * @param endDate - End date for filtering (ISO format)
 * @returns Promise with room blocks data
 * 
 * Created: 08-10-2025 01:56:24
 */
export const getRoomBlocks = async (
  roomId: string,
  startDate: string,
  endDate: string
): Promise<any> => {
  try {
    const queryParams = new URLSearchParams({
      startDate,
      endDate,
    });

    const url = `${API_BASE_URL}/rooms/${roomId}/blocks?${queryParams.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: createHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to fetch room blocks');
    }

    return data.data;
  } catch (error) {
    console.error('[Room API] Error fetching room blocks:', error);
    throw error;
  }
};

/**
 * Get room conflicts
 * Fetches all scheduling conflicts for a specific room and date range
 * 
 * @param roomId - Room ID (UUID)
 * @param startDate - Start date for filtering (ISO format)
 * @param endDate - End date for filtering (ISO format)
 * @returns Promise with room conflicts data
 * 
 * Created: 08-10-2025 01:56:24
 */
export const getRoomConflicts = async (
  roomId: string,
  startDate: string,
  endDate: string
): Promise<any> => {
  try {
    const queryParams = new URLSearchParams({
      startDate,
      endDate,
    });

    const url = `${API_BASE_URL}/rooms/${roomId}/conflicts?${queryParams.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: createHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to fetch room conflicts');
    }

    return data.data;
  } catch (error) {
    console.error('[Room API] Error fetching room conflicts:', error);
    throw error;
  }
};

/**
 * Get unpositioned rooms for a specific floor
 * @param floorId - Floor ID
 * @returns List of rooms without layout
 * @created 15-10-2025 23:00:00
 */
export const getUnpositionedRooms = async (floorId: string): Promise<RoomWithDetails[]> => {
  try {
    const url = `${API_BASE_URL}/rooms/unpositioned/${floorId}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: createHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to fetch unpositioned rooms');
    }

    return data.data.rooms;
  } catch (error) {
    console.error('[Room API] Error fetching unpositioned rooms:', error);
    throw error;
  }
};

/**
 * Interface for creating room with position
 */
export interface CreateRoomWithPositionData {
  floorId: string;
  room: {
    number: string;
    name: string;
    capacity: number;
    templateId?: string;
  };
  position: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

/**
 * Create room with position in atomic transaction
 * @param data - Room and position data
 * @returns Created room and layout
 * @created 15-10-2025 23:02:00
 */
export const createRoomWithPosition = async (
  data: CreateRoomWithPositionData
): Promise<{ room: RoomWithDetails; layout: any }> => {
  try {
    const url = `${API_BASE_URL}/rooms/create-with-position`;

    const response = await fetch(url, {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();
    if (!responseData.success) {
      throw new Error(responseData.error?.message || 'Failed to create room with position');
    }

    return responseData.data;
  } catch (error) {
    console.error('[Room API] Error creating room with position:', error);
    throw error;
  }
};

